import React from 'react';
import { APP_VERSION_INFO } from '../../config/version';

function formatReleaseTimestamp(isoDate: string): string {
  const [datePart, timePart] = isoDate.split('T');
  return timePart ? `${datePart} · ${timePart.slice(0, 5)}` : datePart;
}

/**
 * Every entry's `version` and `date` MUST be a fixed literal, never derived from
 * APP_VERSION_INFO — a dynamic top entry collides (same React key, same content)
 * with the next release's own literal entry the moment the build version reaches
 * it, silently wiping that version's note. See openspec/specs/user-settings/spec.md.
 */
const RELEASES = [
  {
    version: '1.32.0',
    date: '2026-10-06T07:54:35.363Z',
    changes: ['Provider-neutral classification module (#361).'],
  },
  {
    version: '1.31.0',
    date: '2026-09-12T11:06:50.200Z',
    changes: [
      'Complete provider-item-interaction-ux program (drafts, detail shell, honest controls) (#342).',
    ],
  },
  {
    version: '1.30.1',
    date: '2026-09-12T10:05:30.581Z',
    changes: ['Decouple compact context sheet from item selection (#341).'],
  },
  {
    version: '1.30.0',
    date: '2026-09-11T21:29:31.021Z',
    changes: ['Add notification center and recent provider feeds (#338).'],
  },
  {
    version: '1.29.0',
    date: '2026-09-11T19:04:55.225Z',
    changes: ['Add report-only review API and simplify quality gates (#337).'],
  },
  {
    version: '1.28.1',
    date: '2026-09-08T21:57:35.300Z',
    changes: ['Avoid undefined preferred aliases.', 'Omit undefined preferred aliases.'],
  },
  {
    version: '1.28.0',
    date: '2026-09-08T20:04:31.017Z',
    changes: [
      'Finish cross-platform mention flow.',
      'Add Spanish directory autocomplete.',
      'Implement aliases and cross-platform mentions.',
    ],
  },
  {
    version: '1.27.3',
    date: '2026-09-08T18:03:30.333Z',
    changes: ['Recover realtime review delivery.'],
  },
  {
    version: '1.27.2',
    date: '2026-09-08T16:20:57.392Z',
    changes: ['Expose realtime worker progress (#328).'],
  },
  {
    version: '1.27.1',
    date: '2026-09-08T14:56:47.714Z',
    changes: ['Harden async PR review lifecycle.'],
  },
  {
    version: '1.27.0',
    date: '2026-09-08T09:57:26.027Z',
    changes: [
      'Harden cancellation and reconciliation.',
      'Restore review jobs on bootstrap.',
      'Finalize completed review checkpoints.',
      'Validate publication selections.',
      'Cap aggregate batch budget.',
      'Checkpoint background review batches.',
      'Fence concurrent publication claims.',
      'Make review publication idempotent.',
      'Paginate review job recovery.',
      'Configure background review budget.',
      'Persist bounded review checkpoints.',
      'Route durable review publication.',
      'Publish reviews from durable jobs.',
      'Repair missed review notifications.',
      'Reconcile review jobs through source sync.',
      'Harden recovery queries.',
      'Fence cancellation failures.',
      'Fence cancellation and public job state.',
      'Recover interrupted review jobs.',
      'Isolate Docker validation dependencies.',
      'Expose review recovery APIs.',
      'Fence background review workers.',
      'Establish durable review job contracts.',
      'Refresh visible browsers from source index (#314).',
    ],
  },
  {
    version: '1.26.0',
    date: '2026-09-08T04:05:50.586Z',
    changes: ['Unify server-side provider event processing.'],
  },
  {
    version: '1.25.0',
    date: '2026-09-07T18:23:47.564Z',
    changes: ['Run pull request reviews asynchronously.'],
  },
  {
    version: '1.24.1',
    date: '2026-09-07T17:19:19.060Z',
    changes: ['Bind release deploys to matching tags.'],
  },
  {
    version: '1.24.0',
    date: '2026-09-07T15:56:10.380Z',
    changes: ['Harden provider failures and usage controls.'],
  },
  {
    version: '1.23.0',
    date: '2026-09-07T13:48:14.183Z',
    changes: [
      'Report what each route answered, not just how often it was tried.',
      'Report the fallback that answered, and bound what a bloated day can cost.',
      'Keep the usage read off the AI action budget and record what the spec requires.',
      'Record AI usage per provider, model, task and day, and show it in Settings.',
      'Degrade the model catalog when a client cannot list models.',
      'Expose the per-task fallback and stop a pin from reading as inherited.',
      'Stop promising a retry in seconds when the AI quota is exhausted.',
      'Follow the provider when it changes and stop blur from stealing a click.',
      'Make a cleared AI override actually persist and stop sharing one provider session.',
      'Configure provider, model, effort, tone and prompt per AI task.',
      'Route each AI task to its own provider, model, effort and prompt.',
    ],
  },
  {
    version: '1.22.0',
    date: '2026-09-07T06:48:16.519Z',
    changes: [
      'Drop an unused eslint-disable directive in the sw.js test.',
      "Make urgent pushes stand out since browsers won't let us pick a custom sound.",
      'Refuse stale AI approvals and keep scope drafts intact.',
      'Harden AI review publishing, push subscriptions and alert scope.',
      'Proactive push alerts with a competence profile and one-click AI PR review.',
    ],
  },
  {
    version: '1.21.3',
    date: '2026-09-07T01:09:35.636Z',
    changes: ['Key failure notices like commits and prefer the newest Gmail notice in the SPA.'],
  },
  {
    version: '1.21.2',
    date: '2026-09-07T00:46:54.244Z',
    changes: ['Strip undefined leaves before committing a synchronized view.'],
  },
  {
    version: '1.21.1',
    date: '2026-09-07T00:30:03.762Z',
    changes: ['Bind OAuth secrets on the sync workers and keep release commits Prettier-clean.'],
  },
  {
    version: '1.21.0',
    date: '2026-09-07T00:04:02.656Z',
    changes: ['Backfill mailbox connections for existing Gmail accounts and enable source-sync.'],
  },
  {
    version: '1.20.0',
    date: '2026-09-06T20:09:48.801Z',
    changes: [
      'Unbind the legacy Gmail push secret and let auto-tag-release run on scoped merges.',
      'Let auto-release push past the pre-push hook and un-pin the changelog test.',
      'Validate push subscription, drop in-PR version bump, and raise sync coverage.',
      'Harden OIDC fail-closed, recovery reconciliation, and connection rotation.',
      'Strip GIT_* env vars before spawning git in a throwaway repo.',
      'Resolve audit findings for OIDC security, store fencing, and sync polling.',
      'Fail closed on a malformed package.json in the version gate.',
      'Correct the version-bump gate and wire up Release/deploy.',
      'Harden worker lease release, query indexing, cache freshness, and client cleanups.',
      'Integrate source-sync subscription hook, sync chip, and release v1.20.0.',
      'Add GET /api/sync/changes long-poll route and local BFF sync dispatch.',
      'Wire syncRevision view reads, rules version rotation, and syncAdapter in cloud functions.',
      'Add history draining, bounded hydration, and syncAdapter convergence loop.',
      'Add OIDC push authentication, mailbox membership, and durable ingestion route.',
      'Strictly enforce exact /api/gmail/push and validated pubsub envelope.',
      'Add sourceSync store, worker, recovery scheduler, and cloud functions.',
      'Add sourceSync policy, types, feature flags, and indexes.',
      'Add sourceSync contracts and validation.',
      'Fail closed when push webhook secret is unconfigured.',
      'Real-time sync via Pub/Sub push (#268).',
      'Move the version bump out of the PR entirely.',
    ],
  },
  {
    version: '1.19.2',
    date: '2026-09-06T00:00:00.000Z',
    changes: [
      'Improved connection error diagnostics for Slack, GitHub, and Jira in Settings — each failed connection check now shows a specific reason (e.g. invalid token, expired OAuth session, not configured) instead of a generic error.',
    ],
  },
  {
    version: '1.19.1',
    date: '2026-09-05T08:20:53.039Z',
    changes: [
      'Clarify the mobile compact sidebar with a full-width separator and make its touch targets respond faster to taps (#248).',
      'Make Novedades changelog entries mandatory and automatic.',
    ],
  },
  {
    version: '1.19.0',
    date: '2026-09-03T20:38:59-05:00',
    changes: [
      'Gmail ahora agrupa los correos por hilo completo (estilo Slack) en vez de mensajes sueltos, y se corrigió un error que dejaba vacío el destinatario al responder.',
    ],
  },
  {
    version: '1.18.3',
    date: '2026-09-03T19:18:06-05:00',
    changes: [
      'Se agregó el botón de expandir/contraer que faltaba en las tarjetas de Gmail, para igualar el comportamiento de Jira, GitHub y Slack.',
    ],
  },
  {
    version: '1.18.2',
    date: '2026-09-03T16:30:00-05:00',
    changes: [
      'Se corrigió el historial de Novedades: las versiones anteriores dejaban de verse porque la entrada más reciente usaba la versión actual en lugar de un valor fijo, y colisionaba con la siguiente. Cada entrada ahora es fija e incluye la hora.',
    ],
  },
  {
    version: '1.18.0',
    date: '2026-09-03T15:06:23-05:00',
    changes: [
      'Se promovió Gmail a una fuente conectable dedicada con vista propia y se corrigió el probe de conexión para dejar de enmascarar errores transitorios.',
    ],
  },
  {
    version: '1.17.0',
    date: '2026-09-03T12:52:17-05:00',
    changes: [
      'Se estabilizó la barra lateral y el drawer del layout móvil (animaciones, triggers, recorte de contenido) y se agregó Knowledge Wiki como quinta pestaña de navegación inferior.',
    ],
  },
  {
    version: '1.16.0',
    date: '2026-09-03T12:13:11-05:00',
    changes: [
      'Se refactorizó el kit de proveedores y se agregó Gmail como proveedor OAuth conectable.',
    ],
  },
  {
    version: '1.15.0',
    date: '2026-09-03T08:06:43-05:00',
    changes: ['Se agregaron tooltips a los botones de solo ícono para mejorar la descubribilidad.'],
  },
  {
    version: '1.14.1',
    date: '2026-09-03T07:58:42-05:00',
    changes: [
      'Se optimizó el renderizado de listas filtradas/agrupadas y de notas, y se mejoró la accesibilidad de íconos decorativos.',
    ],
  },
  {
    version: '1.14.0',
    date: '2026-09-03T07:31:13-05:00',
    changes: [
      'Se agregó el botón de Filtros reutilizable multi-eje para Radar, Jira, GitHub, Slack y Agentes.',
    ],
  },
  {
    version: '1.13.1',
    date: '2026-09-03T05:46:17-05:00',
    changes: ['Se centralizó la lógica de filtrado de feeds de proveedores.'],
  },
  {
    version: '1.13.0',
    date: '2026-09-03T00:53:36-05:00',
    changes: [
      'Se unificó el panel de conexión de proveedores en Ajustes y se agregó la vista 3D del Wiki de conocimiento (Akasha).',
    ],
  },
  {
    version: '1.12.3',
    date: '2026-09-03T00:35:26-05:00',
    changes: [
      'Se unificaron las pantallas de conexión de proveedores no configurados en un solo panel.',
    ],
  },
  {
    version: '1.11.3',
    date: '2026-09-01T23:03:00-05:00',
    changes: [
      'Se agregó el bypass de autenticación para desarrollo local de agentes con protocolos de prueba.',
    ],
  },
  {
    version: '1.11.2',
    date: '2026-09-01T15:02:11-05:00',
    changes: ['Se optimizó la estrategia de costos de GitHub Actions en CI.'],
  },
  {
    version: '1.11.1',
    date: '2026-08-26T20:05:28-05:00',
    changes: [
      'Se restauró el acceso a Knowledge Wiki en la navegación y se estabilizó la base de CI de tests y typecheck.',
    ],
  },
  {
    version: '1.11.0',
    date: '2026-08-26T06:47:11-05:00',
    changes: ['Se aisló la data de demostración detrás de un parámetro de consulta granular.'],
  },
  {
    version: '1.10.1',
    date: '2026-08-26T06:06:22-05:00',
    changes: [
      'Se pausó temporalmente el OAuth de proveedores externos por una regresión de autenticación.',
    ],
  },
  {
    version: '1.10.0',
    date: '2026-08-24T06:57:25-05:00',
    changes: [
      'Se estandarizó el contrato de OAuth de proveedores y se habilitó el script auxiliar de OAuth de Google.',
    ],
  },
  {
    version: '1.9.1',
    date: '2026-08-23T21:54:18-05:00',
    changes: [
      'Se agregó la sincronización explícita de mensajes de Slack y se recuperó la autenticación OAuth de fuentes externas.',
    ],
  },
  {
    version: '1.9.0',
    date: '2026-08-24T00:00:00-05:00',
    changes: [
      'Se recuperó la autenticación OAuth de Slack, GitHub y Jira y se corrigieron los estados de conexión de proveedores.',
    ],
  },
  {
    version: '1.8.0',
    date: '2026-08-23T00:00:00-05:00',
    changes: [
      'Se corrigieron los callbacks OAuth de fuentes externas y la persistencia segura de credenciales.',
    ],
  },
  {
    version: '1.7.0',
    date: '2026-08-21T00:00:00-05:00',
    changes: [
      'Se reorganizó la navegación por capacidades para encontrar más rápido Radar, Fuentes y herramientas.',
    ],
  },
  {
    version: '1.6.1',
    date: '2026-08-20T00:00:00-05:00',
    changes: ['Se aplicaron ajustes de endurecimiento y estabilidad en la experiencia de Zunelo.'],
  },
  {
    version: '1.6.0',
    date: '2026-08-20T00:00:00-05:00',
    changes: ['El Daily ahora puede enviar el standup directamente a Slack.'],
  },
  {
    version: '1.5.0',
    date: '2026-08-20T00:00:00-05:00',
    changes: [
      'Se agregó el grafo de conocimiento del LLM Wiki para explorar relaciones entre páginas.',
    ],
  },
  {
    version: '1.4.44',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se mejoró la coherencia del asistente de chat y sus enlaces de contexto.'],
  },
  {
    version: '1.4.43',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se completó la revisión de aislamiento y calidad de la aplicación.'],
  },
  {
    version: '1.4.42',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se habilitó el acceso autenticado multiusuario a las fuentes externas.'],
  },
  {
    version: '1.4.38',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se mantuvo OAuth de Codex opcional durante el despliegue.'],
  },
  {
    version: '1.4.37',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se configuraron las credenciales de proveedores OAuth para producción.'],
  },
  {
    version: '1.4.36',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se mantuvieron accionables las acciones OAuth en Ajustes.'],
  },
  {
    version: '1.4.35',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se completaron los flujos de conexión de proveedores.'],
  },
  {
    version: '1.4.34',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se agregó el hub de proveedores y las conexiones OAuth en Ajustes.'],
  },
  {
    version: '1.4.33',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se refinó la distribución y alineación del rail de métricas de Radar.'],
  },
  {
    version: '1.4.32',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se unificaron notas y recordatorios dentro del espacio de trabajo.'],
  },
  {
    version: '1.4.31',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se ajustó el editor responsive de notas para cumplir el layout previsto.'],
  },
  {
    version: '1.4.30',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se preservó el ancho del rail de métricas del briefing de Radar.'],
  },
  {
    version: '1.4.29',
    date: '2026-08-19T00:00:00-05:00',
    changes: [
      'Se alineó el briefing de Radar con el layout aprobado: rail vertical de métricas, tarjeta destacada a ancho completo, lista inferior y texto enriquecido sin comillas decorativas.',
    ],
  },
  {
    version: '1.4.28',
    date: '2026-08-19T00:00:00-05:00',
    changes: [
      'Se recuperaron los colores de estado y se ajustó el layout del briefing para eliminar el espacio vacío y mejorar la lectura de las métricas.',
    ],
  },
  {
    version: '1.4.27',
    date: '2026-08-19T00:00:00-05:00',
    changes: [
      'Se reorganizó el briefing de Radar con métricas verticales, una paleta más sobria y detalle expandido para las conversaciones destacadas.',
    ],
  },
  {
    version: '1.4.26',
    date: '2026-08-19T00:00:00-05:00',
    changes: [
      'Se reemplazó el inbox de mensajes sin leer por Asuntos sin atender, con hilos de Slack pendientes y recordatorios próximos o vencidos.',
    ],
  },
  {
    version: '1.4.25',
    date: '2026-08-19T00:00:00-05:00',
    changes: [
      'Se agregó el LLM Wiki autenticado para guardar páginas Markdown personales y consultarlas desde el asistente de Zunelo.',
    ],
  },
  {
    version: '1.4.24',
    date: '2026-08-19T00:00:00-05:00',
    changes: [
      'Se agregó la detección de nuevas versiones con aviso manual y actualización bajo confirmación del usuario.',
    ],
  },
  {
    version: '1.4.23',
    date: '2026-08-19T00:00:00-05:00',
    changes: [
      'Se corrigió la duplicación del fondo en Radar eliminando la capa fija hardcodeada y se añadieron botones directos para eliminar fondos de imagen personalizados.',
      'Se agregó la acción “Agregar al contexto” en el briefing para abrir la búsqueda de contextos del asistente y adjuntar conversaciones sin salir del Radar.',
    ],
  },
  {
    version: '1.4.22',
    date: '2026-08-19T00:00:00-05:00',
    changes: [
      'Se agregaron fondos con imágenes (incluyendo Radar 3D Hero e imágenes personalizadas) y matriz rápida de asignación por pantallas.',
    ],
  },
  {
    version: '1.4.21',
    date: '2026-08-19T00:00:00-05:00',
    changes: [
      'Se agregó la configuración personalizable de fondos en Apariencia por sección y global.',
    ],
  },
  {
    version: '1.4.20',
    date: '2026-08-19T00:00:00-05:00',
    changes: [
      'Se agregaron URLs compartibles para estados de pantalla con restauración de enlaces profundos.',
    ],
  },
  {
    version: '1.4.19',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se agregaron las notas de changelog en Ajustes, accesibles desde la versión.'],
  },
  {
    version: '1.4.18',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se simplificaron las acciones de las tarjetas de contexto en Radar.'],
  },
  {
    version: '1.4.17',
    date: '2026-08-19T00:00:00-05:00',
    changes: ['Se agregaron atajos de teclado configurables en Ajustes.'],
  },
  {
    version: '1.4.16',
    date: '2026-08-18T00:00:00-05:00',
    changes: ['Se agregaron conversaciones no leídas al inbox de Slack.'],
  },
  {
    version: '1.4.15',
    date: '2026-08-18T00:00:00-05:00',
    changes: ['Se deshabilitaron los accesos a Agentes en el sidebar y las tarjetas de Radar.'],
  },
  {
    version: '1.4.14',
    date: '2026-08-18T00:00:00-05:00',
    changes: [
      'Se agregaron conversaciones en la nube con un chat unificado y una lista en el sidebar.',
    ],
  },
] as const;

export const SettingsChangelogSection: React.FC = () => (
  <section data-testid="settings-changelog-section" aria-labelledby="settings-changelog-title">
    <header style={{ marginBottom: 18 }}>
      <h3 id="settings-changelog-title" style={{ margin: 0, fontSize: 20, color: 'var(--z-text)' }}>
        Novedades
      </h3>
      <p style={{ margin: '6px 0 0', color: 'var(--z-text-3)', fontSize: 13 }}>
        Cambios incluidos en cada versión de Zunelo.
      </p>
    </header>
    <div style={{ display: 'grid', gap: 12 }}>
      {RELEASES.map((release) => (
        <article
          key={release.version}
          style={{ padding: 14, border: '1px solid var(--z-border)', borderRadius: 10 }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              alignItems: 'baseline',
            }}
          >
            <strong style={{ color: 'var(--z-text)' }}>
              v{release.version}
              {release.version === APP_VERSION_INFO.version ? ' · Actual' : ''}
            </strong>
            <time style={{ color: 'var(--z-dim)', fontSize: 12 }} dateTime={release.date}>
              {formatReleaseTimestamp(release.date)}
            </time>
          </div>
          <ul
            style={{
              margin: '10px 0 0',
              paddingLeft: 18,
              color: 'var(--z-text-2)',
              lineHeight: 1.5,
            }}
          >
            {release.changes.map((change) => (
              <li key={change}>{change}</li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  </section>
);
